import * as React from "react";
import { Column, SortingRule, Filter } from 'react-table';
import { LazyLoadingAnimation } from "@Primitive/Lazy";
import { EnumType } from '@Primitive/FieldEditors';
import { Property } from "csstype";
import { ToolbarIconInfo } from '@Primitive/ToolbarIconButton';
import { Currency } from '@Utils/Formats.UI';
import { DateFormat, TimeFormat } from '@Utils/Formats';
import { SettingsGroup } from '@Primitive/SettingsListType';
import { LocalizationMap } from '@Primitive/Localization/Localization';
import type { CSVImportSettings } from "./CSVImport";

const ReactTableBase = React.lazy(() => import("./ReactTableBase"));
// ================================================================================================
export enum FilterOperator {
    none = '',
    equal = '=',
    greater = '>',
    less = '<',
    greaterOrEqual = '>=',
    lessOrEqual = '<=',
    notEqual = '!='
}
// ------------------------------------------------------------------------------------------------
export interface DateTimeFilterState {
    operator: FilterOperator;
    datetime: string;
}
// ------------------------------------------------------------------------------------------------
export interface TimeFilterState {
    operator: FilterOperator;
    time: Date;
}
// ------------------------------------------------------------------------------------------------
export interface FilterState {
    operator: FilterOperator;
    value: any;
}
// ------------------------------------------------------------------------------------------------
export enum ColumnType {
    text = 0,
    number = 1,
    boolean = 2,
    price = 3,
    color = 4,
    percentage = 5,
    datetime = 6,
    button = 7,
    time = 8,
    enum = 9,
    editRow = 10,
    img = 11,
    progress = 12,
    dropdown = 13,
    date = 14
}
// ------------------------------------------------------------------------------------------------
export enum IconType {
    add,
    delete,
    edit,
    info,
    custom
}
// ------------------------------------------------------------------------------------------------
export interface ReactTableColumn extends Column {
    key: string;
    isPrimaryKey?: boolean;
    isMandatory?: boolean;
    type?: ColumnType;
    editable?: boolean;
    tooltip?: string;
    title?: string;
    buttonIcon?: IconType;
    buttonStyle?: React.CSSProperties;
    buttonIconStyle?: React.CSSProperties;
    buttonTooltip?: string;
    pivotBy?: boolean;
    checkedIcon?: string;
    uncheckedIcon?: string;
    enumMapping?: EnumType[];
    enumMulti?: boolean;
    enumCreatable?: boolean;
    enumUseDialog?: boolean;
    backgroundColorAccessor?: string;
    fontColorAccessor?: string;
    styleAccessor?: string;
    visibleInAdd?: boolean;
    dropdownMenuMapping?: DropDownItem[];
    progressIsCircle?: boolean;
    progressColor?: string;
    progressWidth?: number;
    progressShowValue?: boolean;
    progressTrailColor?: string;
    progressTrailWidth?: number;
    headerBackgroundColor?: string;

    // Aggregation
    pivotSettings?: PivotSettings;
}
// ------------------------------------------------------------------------------------------------
export enum ToolbarButton {
    none = 0,
    filter = 1,
    wrapText = 2,
    colvis = 4,
    pivotBy = 8,
    add = 16,
    excel = 32,
    addrow = 64,
    CSVImport = 128,
    all = ToolbarButton.filter | ToolbarButton.wrapText | ToolbarButton.colvis | ToolbarButton.pivotBy | ToolbarButton.excel | ToolbarButton.CSVImport
}
// ------------------------------------------------------------------------------------------------
export enum PivotValueSelectionType {
    Max,
    Min,
    Sum,
    Avg,
    Selected
}
// ------------------------------------------------------------------------------------------------
export interface PivotSettings {
    Type: PivotValueSelectionType,
    SelectedBooleanField?: string
}
// ------------------------------------------------------------------------------------------------
export interface ISelectionActionIcon {
    elementIcon: string;
    tooltip: string;
}
// ------------------------------------------------------------------------------------------------
export enum AddDuplicateRowPolicy {
    /**
     * The default one.
     */
    Allow = 'ALLOW',

    /**
     * And show error alert.
     */
    Prevent = 'PREVENT',

    /**
     * Replace the old row.
     */
    Update = 'UPDATE',

    /**
     * Choose what to do.
     */
    Ask = 'ASK'
}
// ------------------------------------------------------------------------------------------------
export interface PageChange {
    pageIndex: number;
    pageSize: number;
    totalPages: number;
    totalRows: number;
}
// ------------------------------------------------------------------------------------------------
export interface DropDownItem {
    key?: string;
    dropdownMenuItems?: any[];

    row?: ReactTableColumn;
    item?: ToolbarIconInfo;
}
// ------------------------------------------------------------------------------------------------
export interface ReactTableProps {
    localizationKey?: string;
    localizationMap?: LocalizationMap;
    height: number;
    data: any[];
    columns: ReactTableColumn[];
    loading?: boolean;
    style?: React.CSSProperties;
    tableStyle?: React.CSSProperties;
    headerStyle?: React.CSSProperties;
    toolbarButtons?: ToolbarButton;
    toolbarIcons?: ToolbarIconInfo[];
    toolbarStyle?: React.CSSProperties;
    toolbarIconSize?: number;
    toolbarHeight?: number;
    editable?: boolean;
    resizable?: boolean;
    selectable?: boolean;
    selectAllTooltip?: string;
    selectAllMode?: number;
    manuallyToggleSelection?: boolean;
    autoSend?: boolean;
    selectionActionIcon?: ISelectionActionIcon;
    selectedRows?: string[];
    singleSelection?: boolean;
    filterEnabled?: boolean;
    wrapTextEnabled?: boolean;
    defaultPageSize?: number;
    pageSizeOptions?: number[];
    defaultImgUrl?: string;
    imgWidth?: number;
    imgHeight?: number;
    objectFit?: Property.ObjectFit;
    currency?: Currency;
    dateFormat?: DateFormat;
    timeFormat?: TimeFormat;
    showSubComponent?: boolean;
    onlyHiddenColumnsInSubcomponent?: boolean;
    pivotSettings?: PivotSettings;
    pagingSupport?: boolean;
    addDuplicateRowPolicy?: AddDuplicateRowPolicy;
    excelFileName?: string;
    csvImportSettings?: CSVImportSettings;
    onCellUpdate?: (props: any, value: any) => void;
    onRowUpdate?: (index: number, values: any) => void;
    onRowSelect?: (rows: number | string[]) => void;
    onSaveEditedRows?: (rows: any[]) => void;
    onSaveAddedRows?: (rows: any[]) => void;
    onSaveRows?: (rows: any[]) => void;
    onSendSelected?: (rows: any[]) => void;
    onColvisChange?: (key: string) => void;
    onFilterClick?: (enabled: boolean) => void;
    onWrapTextClick?: (enabled: boolean) => void;
    onButtonClick?: (key: string, row: any) => void;
    onToolbarAddButtonClick?: () => void;
    onCellClick?: (row: any, columnJsonName: string, eventSrc?: string) => void;
    onToolbarIconClick?: (iconInfo: ToolbarIconInfo) => void;
    onPageChange?: (data: PageChange) => void;
    onDropdownMenuItemClick?: (data: DropDownItem) => void;
    theme?: {
        toolbarIconColor?: string;

        rowFontColor?: string;
        rowLineBottomColor?: string;
        rowLineBottomStyle?: string;
        rowLineBottomWidth?: number;
        rowSelectedBackgroundColor?: string;
        rowEditedBackgroundColor?: string;
        rowAddedBackgroundColor?: string;
        textAlign?: ReactTableTextAlign;

        headFontColor?: string;
        headLineBottomColor?: string;
        headLineBottomStyle?: string;
        headLineBottomWidth?: number;

        selectFontColor?: string;
        selectBackgroundColor?: string;
        selectBackgroundColorFocused?: string;
        selectBackgroundColorSelected?: string;
    },
    serverSideProcessing?: boolean;
    onFetchDataFromServer?: (request: IRequestServerData) => void;
    totalPages?: number;
}
// ------------------------------------------------------------------------------------------------
export interface IRequestServerData {
    page: number;
    pageSize: number;
    sorted: SortingRule[];
    filtered: Filter[];
}
// ------------------------------------------------------------------------------------------------
export interface ReactTableState {
    data: any[];
    originalData: any[];
    columns: ReactTableColumn[];
    selectedRows?: string[];
    selectionEnabled?: boolean;
    filterEnabled: boolean;
    wrapTextEnabled: boolean;
    unsavedChanges: boolean;
    pageIndex: number;
    pageSize: number;
    showEditRowModal: boolean;
    editRowIndex: number;
    editTableRowData: { [key: string]: any };
    openCSVImportDialog: boolean;
    settingsGroup: SettingsGroup[];
    dropdownClickedItem?: any;
    clickedRow?: any;

    // Related to add duplicate row policy.
    // ----------------------------------------
    openPolicyDialog: boolean;
    chosenPolicy: AddDuplicateRowPolicy;
    // ----------------------------------------

    // manual open/close of <IconMenu /> because the child element is a 
    // custom one (<ToolbarIconButton />) and not the <IconButton /> 
    // and this causes an exception (external library)
    // ----------------------------------------
    dropdownMenusVisibility: { [key: string]: string };
    isExcelMenuOpen: boolean;
    isPivotMenuOpen: boolean;
    isColumnsVisibilityMenuOpen: boolean;
    // ----------------------------------------
}
// ------------------------------------------------------------------------------------------------
export enum LocalizationLabelKey {
    filter = 'filter',
    add = 'add',
    wrapText = 'wrap_text',
    columnsVisibility = 'columns_visibility',
    excelDownload = 'excel_download',
    CSVImport = 'csv_import',
    saveChanges = 'save_changes',
    discardChanges = 'discard_changes',
    sendSelected = 'send_selected',
    pivotBy = 'pivot_by',
    previousText = 'previous_text',
    nextText = 'next_text',
    loadingText = 'loading_text',
    noDataText = 'no_data_text',
    pageText = 'page_text',
    ofText = 'of_text',
    rowsText = 'rows_text',
    addrow = 'add_row'
}
// ------------------------------------------------------------------------------------------------
export class ReactTableStatics {
    // --------------------------------------------------------------------------------------------
    static LocalizationTextMapKey: string = 'Primitive_Table';
    // --------------------------------------------------------------------------------------------
    static GetLocalizationDictionary(): LocalizationMap {
        // Init dictionary with static labels.
        return {
            [LocalizationLabelKey.filter]: 'Filter',
            //[LocalizationLabelKey.add]: 'Add',
            [LocalizationLabelKey.wrapText]: 'Wrap text',
            [LocalizationLabelKey.columnsVisibility]: 'Columns visibility',
            [LocalizationLabelKey.excelDownload]: 'Export to...',
            [LocalizationLabelKey.CSVImport]: 'Import CSV file...',
            [LocalizationLabelKey.saveChanges]: 'Save changes',
            [LocalizationLabelKey.discardChanges]: 'Discard changes',
            [LocalizationLabelKey.sendSelected]: 'Send selected',
            //[LocalizationLabelKey.pivotBy]: 'Pivot columns'
            [LocalizationLabelKey.previousText]: 'Previous',
            [LocalizationLabelKey.nextText]: 'Next',
            [LocalizationLabelKey.loadingText]: 'Loading...',
            [LocalizationLabelKey.noDataText]: 'No rows found...',
            [LocalizationLabelKey.pageText]: 'Page',
            [LocalizationLabelKey.ofText]: 'of',
            [LocalizationLabelKey.rowsText]: 'rows',
            [LocalizationLabelKey.addrow]: 'Add row'
        };
    }
    // --------------------------------------------------------------------------------------------
}
// ------------------------------------------------------------------------------------------------
export enum ReactTableTextAlign {
    Left = 'left',
    Center = 'center',
    Right = 'right'
}
// ------------------------------------------------------------------------------------------------
export var ReactTable = (props: ReactTableProps) => {
    return <React.Suspense fallback={<LazyLoadingAnimation />}>
        <ReactTableBase {...props} />
    </React.Suspense>
}
// ------------------------------------------------------------------------------------------------